import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Search } from "lucide-react";

import { useAdminFirmApplicationsQuery } from "../api/firmApi";

const naira = (n?: number | null) =>
  n == null ? "—" : `₦${Number(n).toLocaleString("en-NG")}`;

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

type StatusKey =
  | "all" | "draft" | "pending_payment" | "awaiting_endorsement"
  | "under_review" | "with_committee" | "returned" | "rejected"
  | "ready_for_interview" | "pending_donation" | "donated" | "approved";

const STATUS_TABS: { key: StatusKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "pending_payment", label: "Awaiting Payment" },
  { key: "awaiting_endorsement", label: "Awaiting Endorsement" },
  { key: "under_review", label: "Under Review" },
  { key: "with_committee", label: "With Committee" },
  { key: "returned", label: "Returned" },
  { key: "rejected", label: "Rejected" },
  { key: "ready_for_interview", label: "Ready for Interview" },
  { key: "pending_donation", label: "Donation Due" },
  { key: "donated", label: "Donated" },
  { key: "approved", label: "Approved" },
];

function statusColors(key: StatusKey | string) {
  switch (key) {
    case "approved":
      return { border: "border-green-200", active: "bg-green-600 text-white border-green-600", inactive: "bg-green-50 text-green-800 hover:bg-green-100", pill: "bg-green-100 text-green-800 border-green-200" };
    case "under_review":
      return { border: "border-blue-200", active: "bg-blue-600 text-white border-blue-600", inactive: "bg-blue-50 text-blue-800 hover:bg-blue-100", pill: "bg-blue-100 text-blue-800 border-blue-200" };
    case "with_committee":
      return { border: "border-violet-200", active: "bg-violet-600 text-white border-violet-600", inactive: "bg-violet-50 text-violet-800 hover:bg-violet-100", pill: "bg-violet-100 text-violet-800 border-violet-200" };
    case "rejected":
      return { border: "border-red-200", active: "bg-red-600 text-white border-red-600", inactive: "bg-red-50 text-red-800 hover:bg-red-100", pill: "bg-red-100 text-red-800 border-red-200" };
    case "returned":
      return { border: "border-orange-200", active: "bg-orange-500 text-white border-orange-500", inactive: "bg-orange-50 text-orange-800 hover:bg-orange-100", pill: "bg-orange-100 text-orange-800 border-orange-200" };
    case "awaiting_endorsement":
      return { border: "border-sky-200", active: "bg-sky-600 text-white border-sky-600", inactive: "bg-sky-50 text-sky-800 hover:bg-sky-100", pill: "bg-sky-100 text-sky-800 border-sky-200" };
    case "ready_for_interview":
      return { border: "border-teal-200", active: "bg-teal-600 text-white border-teal-600", inactive: "bg-teal-50 text-teal-800 hover:bg-teal-100", pill: "bg-teal-100 text-teal-800 border-teal-200" };
    case "donated":
      return { border: "border-emerald-200", active: "bg-emerald-600 text-white border-emerald-600", inactive: "bg-emerald-50 text-emerald-800 hover:bg-emerald-100", pill: "bg-emerald-100 text-emerald-800 border-emerald-200" };
    case "pending_donation":
    case "pending_payment":
      return { border: "border-yellow-200", active: "bg-yellow-600 text-white border-yellow-600", inactive: "bg-yellow-50 text-yellow-900 hover:bg-yellow-100", pill: "bg-yellow-100 text-yellow-900 border-yellow-200" };
    case "draft":
      return { border: "border-gray-300", active: "bg-gray-600 text-white border-gray-600", inactive: "bg-gray-50 text-gray-700 hover:bg-gray-100", pill: "bg-gray-100 text-gray-700 border-gray-300" };
    default:
      return { border: "border-black", active: "bg-black text-white border-black", inactive: "bg-white text-gray-800 hover:bg-gray-50", pill: "bg-gray-100 text-gray-800 border-gray-200" };
  }
}

function StatusBadge({ status }: { status?: string }) {
  const s = String(status ?? "draft").toLowerCase();
  const c = statusColors(s);
  return (
    <span className={`inline-flex items-center px-2 py-1 text-xs border rounded-full ${c.pill}`} title={s}>
      {s.replace("_", " ")}
    </span>
  );
}

function CountPill({ tone, count }: { tone: StatusKey; count: number }) {
  const c = statusColors(tone);
  return <span className={`ml-2 inline-flex items-center px-2 py-0.5 text-xs border rounded-full ${c.pill}`}>{count}</span>;
}

function SummaryCard({ label, value, tone }: { label: string; value: number; tone: StatusKey }) {
  const c = statusColors(tone);
  return (
    <div className={`border rounded-xl p-3 ${c.pill}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs font-medium opacity-80">{label}</div>
    </div>
  );
}

/**
 * The firm applications queue — paperwork moving through review.
 *
 * The standing register of firms that came OUT of it is its own screen
 * (FirmRegistryPage, /firm-registry): different question, different lifetime,
 * and folding a firm registered years ago into the queue of what is waiting on
 * the secretariat would bury both.
 */
export default function AdminFirmApplicationsPage() {
  const [statusTab, setStatusTab] = useState<StatusKey>("all");
  const [q, setQ] = useState("");

  const { data, isLoading, isFetching, isError, refetch } = useAdminFirmApplicationsQuery({ per_page: 500 });

  const rows: any[] = useMemo(
    () => (Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []),
    [data]
  );

  const statusCounts = useMemo(() => {
    const m = new Map<StatusKey, number>();
    m.set("all", rows.length);
    for (const r of rows) {
      const k = String(r.status).toLowerCase() as StatusKey;
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [rows]);

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusTab !== "all" && String(r.status).toLowerCase() !== statusTab) return false;
      if (!nq) return true;
      const stateName = r.state?.name ?? (typeof r.state === "string" ? r.state : "");
      const blob = [r.id, r.practice_name, r.user?.name, r.user?.email, r.user?.member?.membership_no, stateName, r.reference]
        .filter(Boolean).join(" ").toLowerCase();
      return blob.includes(nq);
    });
  }, [rows, statusTab, q]);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="bg-white border rounded-xl p-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
              <Building2 className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Firm Applications</h1>
              <p className="text-sm text-gray-500 mt-0.5">Review practice-firm registration applications.</p>
            </div>
          </div>
          <button onClick={() => refetch()} disabled={isFetching} className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm">
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mt-5">
          {STATUS_TABS.map((t) => (
            <SummaryCard key={t.key} label={t.label} value={statusCounts.get(t.key) || 0} tone={t.key} />
          ))}
        </div>

        {/* Search */}
        <div className="mt-5 relative max-w-md">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by firm, applicant, membership no, email…"
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
          />
        </div>

        {/* Status tabs */}
        <div className="flex flex-wrap gap-2 mt-4">
          {STATUS_TABS.map((t) => {
            const active = statusTab === t.key;
            const c = statusColors(t.key);
            return (
              <button
                key={t.key}
                onClick={() => setStatusTab(t.key)}
                className={["px-3 py-2 text-sm border rounded-md transition", active ? c.active : `${c.border} ${c.inactive}`].join(" ")}
              >
                {t.label}
                <CountPill tone={t.key} count={statusCounts.get(t.key) || 0} />
              </button>
            );
          })}
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="mt-6 text-sm text-gray-500">Loading applications…</div>
        ) : isError ? (
          <div className="mt-6 text-sm text-red-600">Failed to load applications.</div>
        ) : filtered.length === 0 ? (
          <div className="mt-6 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">
            No firm applications match your filters.
          </div>
        ) : (
          <div className="mt-5 border rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="px-4 py-2 font-medium">Firm</th>
                  <th className="px-4 py-2 font-medium">Applicant</th>
                  <th className="px-4 py-2 font-medium">Partners</th>
                  <th className="px-4 py-2 font-medium">Fee</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Submitted</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.practice_name ?? "—"}</div>
                      <div className="text-xs text-gray-400">#{String(r.id).padStart(6, "0")}{(r.state?.name ?? (typeof r.state === "string" ? r.state : "")) ? ` · ${r.state?.name ?? r.state}` : ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-700">{r.user?.name ?? "—"}</div>
                      <div className="text-xs text-gray-400">{r.user?.member?.membership_no ?? r.user?.email ?? ""}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {r.partners_count ?? 0}
                      <span className="text-gray-300"> · </span>
                      <span className="text-gray-400">{r.referees_count ?? 0} ref</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{naira(r.amount != null ? Number(r.amount) / 100 : null)}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3 text-gray-500">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/admin/firm-applications/${r.id}`} className="px-3 py-1.5 rounded-lg border text-xs hover:bg-gray-50">
                        Review
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
